import axios from 'axios';
import { getKeycloak } from './keycloak';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api';

export interface RangeOSEnvironment {
  id: string;
  name: string;
  description?: string;

  // API Configuration
  devOpsApiUrl: string;
  graphqlUrl?: string;
  graphqlSubscriptionsUrl?: string;

  // Authentication
  apiKey?: string;
  bearerToken?: string;

  // Player Configuration
  playerUrl?: string;

  // Metadata
  createdAt: string;
  lastUsed?: string;
  status: 'active' | 'inactive';
}

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests (Keycloak)
api.interceptors.request.use(async (config) => {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    config.headers.Authorization = `Bearer ${windowToken}`;
    return config;
  }

  // Fallback to Keycloak instance
  try {
    const keycloak = getKeycloak();
    if (keycloak?.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
  } catch (error) {
    console.warn('Could not get auth token for RangeOS Environment API');
  }

  return config;
});

export const rangeosEnvironmentService = {
  /**
   * Get all RangeOS environments
   */
  async getEnvironments(): Promise<RangeOSEnvironment[]> {
    const response = await api.get('/rangeos/environments');
    return response.data;
  },

  /**
   * Get active environments only
   */
  async getActiveEnvironments(): Promise<RangeOSEnvironment[]> {
    const response = await api.get('/rangeos/environments/active');
    return response.data;
  },

  /**
   * Get a specific environment
   */
  async getEnvironment(id: string): Promise<RangeOSEnvironment> {
    const response = await api.get(`/rangeos/environments/${id}`);
    return response.data;
  },

  /**
   * Create a new environment
   */
  async createEnvironment(data: {
    name: string;
    devOpsApiUrl: string;
    description?: string;
    graphqlUrl?: string;
    graphqlSubscriptionsUrl?: string;
    apiKey?: string;
    bearerToken?: string;
    playerUrl?: string;
  }): Promise<RangeOSEnvironment> {
    const response = await api.post('/rangeos/environments', data);
    return response.data;
  },

  /**
   * Update an environment
   */
  async updateEnvironment(
    id: string,
    data: Partial<Omit<RangeOSEnvironment, 'id' | 'createdAt'>>
  ): Promise<RangeOSEnvironment> {
    const response = await api.put(`/rangeos/environments/${id}`, data);
    return response.data;
  },

  /**
   * Delete an environment
   */
  async deleteEnvironment(id: string): Promise<void> {
    await api.delete(`/rangeos/environments/${id}`);
  },

  /**
   * List scenarios from an environment
   */
  async listScenarios(environmentId: string): Promise<Array<{
    id: string;
    name: string;
    description?: string;
    dateCreated?: string;
    dateEdited?: string;
    author?: string;
    owner?: string;
  }>> {
    const response = await api.get(`/rangeos/environments/${environmentId}/scenarios`);
    return response.data;
  },

  /**
   * Search scenarios in an environment
   */
  async searchScenarios(environmentId: string, query: string): Promise<Array<{
    id: string;
    name: string;
    description?: string;
    dateCreated?: string;
    dateEdited?: string;
    author?: string;
    owner?: string;
  }>> {
    const response = await api.get(`/rangeos/environments/${environmentId}/scenarios/search`, {
      params: { q: query },
    });
    return response.data;
  },

  /**
   * Get AU mappings for specific AUs
   */
  async getAUMappings(environmentId: string, auIds: string[]): Promise<Array<{
    auId: string;
    name: string;
    scenarios: string[];
    dateCreated?: string;
    dateEdited?: string;
    author?: string;
  }>> {
    const response = await api.get(`/rangeos/environments/${environmentId}/au-mappings`, {
      params: { auIds: auIds.join(',') },
    });
    return response.data;
  },

  /**
   * Create or update an AU mapping
   */
  async saveAUMapping(environmentId: string, mapping: {
    auId: string;
    name: string;
    scenarios: string[];
  }): Promise<{
    auId: string;
    name: string;
    scenarios: string[];
  }> {
    const response = await api.post(`/rangeos/environments/${environmentId}/au-mappings`, mapping);
    return response.data;
  },

  /**
   * Import a draft scenario from S3 to a RangeOS environment
   * This creates the scenario in RangeOS so it can be used for AU mappings
   */
  async importDraftFromS3(
    environmentId: string,
    options: {
      s3BucketId: string;
      s3EnvironmentId: string;
      scenarioUuid: string;
      activityId?: number;
    }
  ): Promise<{
    success: boolean;
    message: string;
    draftId: string;
    scenarioId: string;
    draftName: string;
  }> {
    const response = await api.post(
      `/rangeos/environments/${environmentId}/import-draft-from-s3`,
      options
    );
    return response.data;
  },
};
